import {
  advancedStrings as A,
  documentationStrings as D,
  settingsKeysStrings as K,
  settingsStrings as S,
} from './strings'

/**
 * EVERY SETTING, BY THE NAME ITS SHEET GIVES IT.
 *
 * The hub's search finds a single setting, not only a section — "المنفذ", "HSTS", "قفل" —
 * and opening a result lands on that field. So each entry is the label exactly as the
 * sheet prints it: the sheet finds the field by that text.
 */
export const SEARCH_FIELDS = {
  identity: [S.theme.main, S.theme.active],
  media: [
    S.media.whiteLogo,
    S.media.logoBackground,
    S.media.mainLogo,
    S.media.favicon,
    S.media.loginTheme,
    S.media.logoOnLogin,
    S.media.arabicImage,
    S.media.englishImage,
    S.media.imageAr,
    S.media.imageEn,
  ],
  keys: [
    S.keys.appTitle,
    S.sheet.footer,
    S.sheet.orgName,
    S.keys.orgEmail,
    S.keys.domain,
    S.keys.useHsts,
    S.servicesUrl.label,
  ],
  contact: [
    S.contact.enablePage,
    S.contact.pageTitle,
    S.contact.pageHeader,
    S.contact.enableEmail,
    S.contact.emailAddress,
    S.contact.showSendForm,
    S.contact.showEmailInfo,
    S.contact.emailLabel,
    S.contact.showPhone,
    S.contact.phone,
    S.contact.phoneLabel,
  ],
  video: [
    S.video.progressBar,
    S.video.forceSubtitle,
    S.video.userSubtitleControl,
    S.video.pauseOnBlur,
    S.video.playbackRates,
  ],
  userManual: [
    S.userManual.guideAr,
    S.userManual.guideEn,
    S.userManual.popup,
    S.userManual.introVideo,
    S.userManual.linkOnLogin,
    S.userManual.separatePage,
    S.userManual.adminUrl,
  ],
  documentation: [D.installed, D.importTitle],
  notifications: [S.notifications.title],
  email: [
    S.email.host,
    S.email.port,
    S.email.ssl,
    S.email.username,
    S.email.password,
    S.email.skipCertCheck,
    S.email.from,
    S.email.senderName,
    S.email.serviceState,
    S.email.test,
  ],
  messaging: [S.messaging.whatsapp, S.messaging.slack, S.messaging.sms],
  reports: [S.reports.userStatistics, S.sheet.reportColumns],
  login: [
    A.login.licenseType,
    A.login.authMethod,
    A.login.authSource,
    A.login.usernameFormat,
    A.login.otpEnabled,
    A.login.otpTarget,
    A.login.otpLength,
    A.login.otpValidPeriod,
    A.login.lockoutEnabled,
    A.login.attempts,
    A.login.lockoutWindow,
    A.login.sessionTimeout,
    A.login.termsEnable,
    A.login.termsAr,
    A.login.termsEn,
    A.login.termsUpdated,
  ],
  registration: [
    A.registration.activateInvitations,
    A.registration.activateResetPassword,
    A.registration.showRegistrationLink,
    A.registration.activationMethod,
    A.registration.emailOnDomain,
    A.registration.domains,
    A.sheet.registrationLink,
  ],
  profile: [A.profile.title, A.sheet.customNames, A.sheet.openProfile, A.profile.forceGroupLabel],
  sources: ['LDAP', 'Microsoft Entra ID', A.sources.newSource],
  sync: [
    A.sheet.syncSources,
    A.sync.deptsAutoFetch,
    A.sync.usersAutoFetch,
    A.sync.overLicense,
    A.sync.deleteEmptyGroups,
    A.sync.commandMode,
  ],
  servers: [A.servers.newServer, A.servers.serverUrl],
  services: [
    A.services.enableNotificationService,
    A.services.maxRetries,
    A.services.maxTaskMinutes,
    A.sheet.serviceStatus,
  ],
  settingsKeys: [K.reference.title],
}

/**
 * Text as a search compares it. Arabic is typed in more than one spelling of the same
 * word — أ/إ/آ for ا, ى for ي, ة for ه, with or without tashkeel — and a search that only
 * matched the exact letters would miss half of what an administrator types.
 */
export function normalize(text) {
  return String(text ?? '')
    .toLowerCase()
    .replace(/[ً-ْٰـ]/g, '')
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه')
    .replace(/ؤ/g, 'و')
    .replace(/ئ/g, 'ي')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * The settings a query finds, in the order the hub lists them: the section itself when its
 * name matches, then each of its settings whose name does.
 *
 * @param {{title: string, items: object[]}[]} groups  the hub's groups, as navFor gives them
 */
export function searchSettings(groups, query) {
  const needle = normalize(query)
  if (!needle) return []
  const results = []
  for (const group of groups) {
    for (const item of group.items) {
      if (normalize(item.label).includes(needle)) results.push({ item, group, label: null })
      for (const label of SEARCH_FIELDS[item.id] ?? []) {
        if (normalize(label).includes(needle)) results.push({ item, group, label })
      }
    }
  }
  return results.slice(0, 40)
}
